import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { sendOtp, verifyOtp, signup } from '../../../services';
import { ROUTES } from '../../../constants/routes';
import leftPanelImg from '../../../assets/images/auth-popup-left.svg';
import logoPng from '../../../assets/images/logo.png';
import mailIcon from '../../../assets/images/mail.svg';

import { AGE_GROUP_OPTIONS, GENDER_OPTIONS } from '../../../constants/profileFormOptions';
import { useDistricts } from '../../../hooks/useDistricts';

function normalizeMobile(value) {
  return value.replace(/\D/g, '').slice(0, 10);
}

function PersonIcon() {
  return (
    <svg className="h-4 w-4 shrink-0 text-[#9369CC]" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h12" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l6 6-6 6" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H7" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 6l-6 6 6 6" />
    </svg>
  );
}

function IndiaFlagIcon() {
  return (
    <svg viewBox="0 0 24 16" className="h-4 w-6" aria-hidden>
      <rect width="24" height="5.33" fill="#FF9933" />
      <rect y="5.33" width="24" height="5.34" fill="#FFFFFF" />
      <rect y="10.67" width="24" height="5.33" fill="#138808" />
      <circle cx="12" cy="8" r="2.2" fill="none" stroke="#000080" strokeWidth="0.4" />
      <circle cx="12" cy="8" r="0.35" fill="#000080" />
    </svg>
  );
}

export default function AuthPopup() {
  const { isLoggedIn, authPopupOpen, closeAuthPopup, login } = useAuthContext();
  const [mobile, setMobile] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [status, setStatus] = useState({ state: 'idle', message: '' }); // idle | loading | error
  const [toast, setToast] = useState(null); // { message, type: 'success' | 'error' }
  const [step, setStep] = useState('mobile'); // mobile | otp | signup
  const [otp, setOtp] = useState(['', '', '', '']);
  const [otpToken, setOtpToken] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [signupForm, setSignupForm] = useState({
    fullName: '',
    email: '',
    dateOfBirth: '',
    gender: '',
    ageGroup: '',
    district: '',
  });

  const mobileValid = useMemo(() => mobile.length === 10, [mobile]);
  const otpValue = useMemo(() => otp.join(''), [otp]);
  const otpValid = useMemo(() => otpValue.length === 4 && /^\d{4}$/.test(otpValue), [otpValue]);
  const { districts, loading: districtsLoading } = useDistricts({ enabled: step === 'signup' });
  const signupValid = useMemo(
    () =>
      signupForm.fullName.trim().length >= 2 &&
      signupForm.dateOfBirth &&
      signupForm.gender &&
      signupForm.ageGroup &&
      signupForm.district,
    [signupForm]
  );

  const dismiss = () => {
    closeAuthPopup();
  };

  function showToast(message, type = 'success') {
    setToast({ message, type });
  }

  function goBack() {
    if (status.state === 'loading') return;
    if (step === 'signup') {
      setStatus({ state: 'idle', message: '' });
      setStep('otp');
      return;
    }
    if (step === 'otp') {
      setStatus({ state: 'idle', message: '' });
      setOtp(['', '', '', '']);
      setStep('mobile');
    }
  }

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!authPopupOpen) return;
    setStep('mobile');
    setStatus({ state: 'idle', message: '' });
    setToast(null);
    setOtp(['', '', '', '']);
    setOtpToken('');
    setResendIn(0);
    setSignupForm({
      fullName: '',
      email: '',
      dateOfBirth: '',
      gender: '',
      ageGroup: '',
      district: '',
    });
  }, [authPopupOpen]);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape') dismiss();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  async function handleContinue() {
    if (!mobileValid || !accepted) return;
    setStatus({ state: 'loading', message: '' });
    try {
      const res = await sendOtp(mobile);
      const token = res?.data?.token;
      if (token) {
        setOtpToken(token);
        try {
          window.sessionStorage.setItem('chse_otp_token', token);
          window.sessionStorage.setItem('chse_otp_mobile', mobile);
        } catch {
          // ignore
        }
      }
      setStatus({ state: 'idle', message: '' });
      showToast(res?.message || 'OTP sent successfully');
      setStep('otp');
      setResendIn(15);
    } catch (e) {
      setStatus({ state: 'error', message: e?.message || 'Failed to send OTP' });
    }
  }

  useEffect(() => {
    if (step !== 'otp') return;
    if (resendIn <= 0) return;
    const t = window.setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(t);
  }, [resendIn, step]);

  function setOtpAt(index, value) {
    const digit = value.replace(/\D/g, '').slice(-1);
    setOtp((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
  }

  function handleOtpKeyDown(index, e) {
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      const prev = document.getElementById(`otp-${index - 1}`);
      prev?.focus();
    }
  }

  function handleOtpChange(index, e) {
    const val = e.target.value;
    const digit = val.replace(/\D/g, '').slice(-1);
    setOtpAt(index, digit);
    if (digit && index < 3) {
      const next = document.getElementById(`otp-${index + 1}`);
      next?.focus();
    }
  }

  async function handleVerifyOtp() {
    if (!otpValid) return;
    setStatus({ state: 'loading', message: '' });
    try {
      const token = otpToken || window.sessionStorage.getItem('chse_otp_token') || '';
      if (!token) {
        setStatus({ state: 'error', message: 'Session expired. Please request OTP again.' });
        return;
      }
      const res = await verifyOtp({
        token,
        otpCode: otpValue,
      });

      const successMessage = res?.message || 'OTP verified successfully';
      setStatus({ state: 'idle', message: '' });
      showToast(successMessage);

      if (res?.data?.user_exists) {
        window.setTimeout(() => {
          login(res?.data?.token);
          closeAuthPopup();
        }, 1200);
        return;
      }

      setStep('signup');
    } catch (e) {
      setStatus({ state: 'error', message: e?.message || 'OTP verification failed' });
    }
  }

  async function handleSignup() {
    if (!signupValid) return;
    setStatus({ state: 'loading', message: '' });
    try {
      const token = otpToken || window.sessionStorage.getItem('chse_otp_token') || '';
      if (!token) {
        setStatus({ state: 'error', message: 'Session expired. Please request OTP again.' });
        return;
      }
      const res = await signup({
        token,
        fullName: signupForm.fullName.trim(),
        email: signupForm.email,
        dateOfBirth: signupForm.dateOfBirth,
        gender: signupForm.gender,
        ageGroup: signupForm.ageGroup,
        district: signupForm.district,
      });
      setStatus({ state: 'idle', message: '' });
      showToast(res?.message || 'Account created successfully');
      login(res?.data?.token);
      closeAuthPopup();
    } catch (e) {
      setStatus({ state: 'error', message: e?.message || 'Signup failed' });
    }
  }

  function updateSignupField(field, value) {
    setSignupForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleResend() {
    if (resendIn > 0) return;
    setStatus({ state: 'loading', message: '' });
    try {
      const res = await sendOtp(mobile);
      const token = res?.data?.token;
      if (token) {
        setOtpToken(token);
        try {
          window.sessionStorage.setItem('chse_otp_token', token);
          window.sessionStorage.setItem('chse_otp_mobile', mobile);
        } catch {
          // ignore
        }
      }
      setStatus({ state: 'idle', message: '' });
      showToast(res?.message || 'OTP resent successfully');
      setResendIn(15);
    } catch (e) {
      setStatus({ state: 'error', message: e?.message || 'Failed to resend OTP' });
    }
  }

  if (isLoggedIn || !authPopupOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#2D2C2CED]/90 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-popup-title"
    >
      {toast && (
        <div
          role="status"
          className={`fixed left-1/2 top-6 z-[60] -translate-x-1/2 rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-lg ${toast.type === 'error' ? 'bg-red-600' : 'bg-[#2e7d32]'
            }`}
        >
          {toast.message}
        </div>
      )}
      <div className="relative flex w-full max-w-[1000px] h-auto sm:h-[600px] max-h-[90vh] overflow-auto sm:overflow-hidden rounded-2xl bg-white shadow-2xl">
        <button
          type="button"
          aria-label="Close"
          onClick={dismiss}
          className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-[#CCCCCC] text-[#1B1B1B] text-2xl"
        >
          ×
        </button>

        <div className="relative hidden w-[567px] shrink-0 overflow-hidden sm:block">
          <img
            src={leftPanelImg}
            alt=""
            className="h-full w-[calc(100%+2px)] max-w-none object-cover object-left sm:min-h-[600px]"
          />
        </div>

        <div className="relative z-[1] ml-0 flex w-full flex-1 flex-col items-center bg-white py-8 pl-2 pr-2 sm:-ml-14 sm:w-[421px] sm:pl-0 sm:pr-6">
          <div className="flex w-full max-w-[320px] flex-col items-center sm:max-w-[340px]">
            {step !== 'mobile' && (
              <div className="mt-1 flex w-full items-center justify-start">
                <button
                  type="button"
                  onClick={goBack}
                  disabled={status.state === 'loading'}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7E57C2] disabled:opacity-50"
                >
                  <ArrowLeftIcon />
                  <span className="underline">Back</span>
                </button>
              </div>
            )}
            <div className="flex flex-col items-center text-center">
              <img
                src={logoPng}
                alt="CHSE express"
                className="mt-8 h-[82px] w-[140px] sm:mt-10 sm:h-[95px] sm:w-[164px]"
              />
            </div>

            {step === 'mobile' && (
              <>
                <h2 id="auth-popup-title" className="mt-3 text-[14px] font-extrabold text-[#9369CC] sm:text-base">
                  Odisha&apos;s Learning Revolution.
                </h2>
                <p className="mt-5 text-center text-[12px] font-medium leading-relaxed text-[#666666]">
                  Start your learning journey with CHSE Express.
                  <span className="mt-1 block">Log in or Create an account to get started.</span>
                </p>
              </>
            )}

            {step === 'otp' && (
              <>
                <h2
                  id="auth-popup-title"
                  className="mt-6 text-center text-[22px] font-extrabold uppercase tracking-wide text-[#9369CC]"
                >
                  OTP VERIFICATION
                </h2>
                <p className="mt-3 text-center text-[11px] font-medium leading-relaxed text-[#666666]">
                  Verify your identity with the OTP.
                  <br />
                  Check your phone for the code we sent.
                  <br />
                  Enter it below to proceed.
                </p>
              </>
            )}

            {step === 'signup' && (
              <>
                <h2
                  id="auth-popup-title"
                  className="mt-3 text-center text-[22px] font-extrabold uppercase tracking-wide text-[#9369CC]"
                >
                  Tell Us About You
                </h2>
                <p className="mt-2 text-center text-[11px] font-medium text-[#000000]">
                  Fill in the required information to continue.
                </p>
              </>
            )}

            {step === 'mobile' && (
              <div className="mt-10 w-full sm:mt-16">
                <label htmlFor="mobile" className="sr-only">
                  Enter Your Mobile No
                </label>
                <div className="flex w-full items-stretch gap-2">
                  <div
                    className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-lg border-2 border-[#009DA8] bg-white"
                    aria-hidden
                  >
                    <IndiaFlagIcon />
                  </div>

                  <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border-2 border-[#009DA8] bg-white px-3 py-2">
                    <span className="shrink-0 text-sm font-medium text-[#1B1B1B]">+91</span>
                    <input
                      id="mobile"
                      value={mobile}
                      inputMode="numeric"
                      placeholder="Enter Your Mobile No"
                      onChange={(e) => setMobile(normalizeMobile(e.target.value))}
                      className="min-w-0 flex-1 bg-transparent text-sm text-[#1B1B1B] outline-none placeholder:text-[#999999]"
                    />
                    {mobile.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setMobile('')}
                        aria-label="Clear mobile number"
                        className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#CCCCCC] text-[11px] leading-none text-white hover:bg-[#AAAAAA]"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {step === 'otp' && (
              <div className="mt-4 w-full sm:mt-4">
                <div className="mt-7 flex items-center justify-center gap-4">
                  {otp.map((d, i) => (
                    <input
                      key={i}
                      id={`otp-${i}`}
                      value={d}
                      inputMode="numeric"
                      maxLength={1}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      onChange={(e) => handleOtpChange(i, e)}
                      className="h-[34px] w-[34px] rounded-lg border border-[#B8A7DD] bg-[#F2EAFE] text-center text-[14px] font-semibold text-[#7E57C2] outline-none focus:border-[#9369CC] focus:bg-white"
                    />
                  ))}
                </div>

                <div className="mt-3 flex w-full items-center justify-around px-6 text-[11px] font-semibold text-[#9369CC]">
                  <span className="">
                    Didn&apos;t get the OTP?
                  </span>
                  {resendIn > 0 ? (
                    <span className="text-[#999999]">Resend in {resendIn}s</span>
                  ) : (
                    <button type="button" onClick={handleResend} className="">
                      Resend
                    </button>
                  )}
                </div>
              </div>
            )}

            {step === 'signup' && (
              <div className="mt-6 w-full space-y-4">
                <div className="flex items-center gap-2 rounded-lg border-2 border-[#CCCCCC] bg-white px-3 py-2">
                  <PersonIcon />
                  <input
                    type="text"
                    value={signupForm.fullName}
                    onChange={(e) => updateSignupField('fullName', e.target.value)}
                    placeholder="Full Name"
                    className="w-full bg-transparent text-[12px] text-[#1B1B1B] outline-none placeholder:text-[#999999]"
                  />
                </div>

                <div className="flex items-center gap-2 rounded-lg border-2 border-[#CCCCCC] bg-white px-3 py-2">
                  <img src={mailIcon} alt="" className="h-4 w-4 shrink-0 text-[#9369CC]" />
                  <input
                    type="email"
                    value={signupForm.email}
                    onChange={(e) => updateSignupField('email', e.target.value)}
                    placeholder="Email Address ( Optional)"
                    className="w-full bg-transparent text-[12px] text-[#1B1B1B] outline-none placeholder:text-[#999999]"
                  />
                </div>

                <div className="flex items-center gap-2 rounded-lg border-2 border-[#CCCCCC] bg-white px-3 py-2">
                  <input
                    type="date"
                    value={signupForm.dateOfBirth}
                    onChange={(e) => updateSignupField('dateOfBirth', e.target.value)}
                    placeholder='DOB'
                    className="w-full bg-transparent text-[12px] text-[#999999] outline-none placeholder:text-[#999999]"
                  />
                </div>

                <div className="flex items-center gap-2 rounded-lg border-2 border-[#CCCCCC] bg-white px-3 py-2">
                  <select
                    value={signupForm.ageGroup}
                    onChange={(e) => updateSignupField('ageGroup', e.target.value)}
                    className="w-full bg-transparent text-[12px] text-[#999999] outline-none"
                  >
                    <option value="">Age Group</option>
                    {AGE_GROUP_OPTIONS.map((group) => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                  </select>
                </div>

                <div className='flex flex-row gap-2 w-full'>
                  <div className="w-1/2 flex items-center gap-2 rounded-lg border-2 border-[#CCCCCC] bg-white px-3 py-2">
                    <select
                      value={signupForm.gender}
                      onChange={(e) => updateSignupField('gender', e.target.value)}
                      className="w-full bg-transparent text-[12px] text-[#999999] outline-none placeholder:text-[#999999]"
                    >
                      <option className='text-[#999999]' value="">Gender</option>
                      {GENDER_OPTIONS.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                  </div>


                  <div className="w-1/2 flex items-center gap-2 rounded-lg border-2 border-[#CCCCCC] bg-white px-3 py-2">
                    <select
                      value={signupForm.district}
                      onChange={(e) => updateSignupField('district', e.target.value)}
                      disabled={districtsLoading}
                      className="w-full bg-transparent text-[12px] text-[#999999] outline-none disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <option value="">{districtsLoading ? 'Loading...' : 'Dist'}</option>
                      {districts.map((district) => (
                        <option key={district.id} value={district.name}>
                          {district.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {step === 'mobile' && (
              <label className="mt-4 flex w-full items-center gap-2">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="h-4 w-4 shrink-0 accent-[#6D39C3]"
                />
                <span className="text-left text-[10px] leading-snug text-[#1B1B1B]">
                  I&apos;ve read and agree with the{' '}
                  <Link
                    to={ROUTES.POLICY('terms')}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="font-semibold underline-offset-2 hover:underline"
                  >
                    Terms of services
                  </Link>
                  ,{' '}
                  <Link
                    to={ROUTES.POLICY('privacy')}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="font-semibold underline-offset-2 hover:underline"
                  >
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
            )}

            {(status.state === 'loading' || status.state === 'error') && (
              <div
                className={`mt-3 w-full rounded-lg px-3 py-2 text-[12px] font-medium ${status.state === 'error' ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'
                  }`}
              >
                {status.state === 'loading'
                  ? step === 'mobile'
                    ? 'Sending OTP...'
                    : step === 'otp'
                      ? 'Verifying OTP...'
                      : 'Creating account...'
                  : status.message}
              </div>
            )}

            <button
              type="button"
              disabled={
                status.state === 'loading' ||
                (step === 'mobile' && (!mobileValid || !accepted)) ||
                (step === 'otp' && !otpValid) ||
                (step === 'signup' && !signupValid)
              }
              onClick={
                step === 'mobile' ? handleContinue : step === 'otp' ? handleVerifyOtp : handleSignup
              }
              className={`inline-flex h-[36px] w-[208px] items-center justify-center gap-2 rounded bg-gradient-to-t from-[#6A74E1] to-[#B539FD] text-[12px] font-semibold uppercase tracking-wide text-white shadow disabled:cursor-not-allowed disabled:opacity-50 ${step === 'signup' ? 'mt-6' : 'mt-12 sm:mt-16'
                }`}
            >
              {status.state === 'loading' ? 'Please wait...' : 'Continue'}
              <span aria-hidden className="text-white/95">
                <ArrowRightIcon />
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
